// DP262 — the public page behind a shared memo. Standalone by design: one
// unauthenticated GET, no app chrome, no assistant. What it renders is the
// snapshot taken when the link was made, so a later rewrite of the memo does
// not change what the recipient was told.
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Check, FileText, Square } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const prettyDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString([], { year: 'numeric', month: 'long', day: 'numeric' });
};

// The memo body is stored as the editor's HTML. Strip it to text rather than
// trusting it into the page — this document is read by people outside the
// account and the snapshot travelled through a share link.
const asText = (html) => String(html || '')
  .replace(/<br\s*\/?>/gi, '\n')
  .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
  .replace(/<[^>]*>/g, '')
  .replace(/&nbsp;/g, ' ')
  .replace(/&amp;/g, '&')
  .replace(/&lt;/g, '<')
  .replace(/&gt;/g, '>')
  .replace(/\n{3,}/g, '\n\n')
  .trim();

export default function MemoShareViewer() {
  const { token } = useParams();
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/note-shares/${token}`);
        if (live) setShare(r.data);
      } catch (err) {
        const code = err.response?.status;
        if (live) {
          if (code === 410) setError('This memo link has expired. Ask whoever sent it for a fresh one.');
          else if (code === 404) setError('This memo link is no longer available. It may have been revoked.');
          else setError('This memo could not be loaded. Please try again shortly.');
        }
      } finally {
        if (live) setLoading(false);
      }
    };
    if (token) load();
    return () => { live = false; };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50" data-testid="memo-share-loading">
        <p className="text-sm text-stone-500">Loading the memo…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 px-6" data-testid="memo-share-error">
        <div className="max-w-md text-center">
          <FileText className="w-10 h-10 mx-auto mb-4 text-stone-400" aria-hidden="true" />
          <h1 className="text-lg font-semibold text-stone-800 mb-2">Memo unavailable</h1>
          <p className="text-sm text-stone-600" data-testid="memo-share-error-text">{error}</p>
          <p className="text-xs text-stone-400 mt-8">Shared from KEY-MADE</p>
        </div>
      </div>
    );
  }

  const memo = share?.memo || {};
  const body = asText(memo.content);
  const todos = memo.todos || [];
  const photos = memo.photos || [];

  return (
    <div className="min-h-screen bg-stone-50" data-testid="memo-share-page">
      <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6 sm:py-12">
        {share?.company_name && (
          <p className="text-xs uppercase tracking-widest text-stone-500 mb-2" data-testid="memo-share-company">
            {share.company_name}
          </p>
        )}
        <h1 className="text-2xl font-semibold text-stone-900" data-testid="memo-share-title">
          {memo.title || 'Untitled memo'}
        </h1>
        <p className="mt-2 text-sm text-stone-500" data-testid="memo-share-meta">
          {[share?.project?.name, prettyDate(memo.created_at)].filter(Boolean).join(' · ')}
        </p>

        {share?.sender_note && (
          <div className="mt-8 border-l-2 border-stone-300 pl-4" data-testid="memo-share-note">
            <p className="text-sm text-stone-700 whitespace-pre-wrap">{share.sender_note}</p>
          </div>
        )}

        {body && (
          <p className="mt-8 text-base leading-relaxed text-stone-800 whitespace-pre-wrap" data-testid="memo-share-body">
            {body}
          </p>
        )}

        {todos.length > 0 && (
          <ul className="mt-8 space-y-2" data-testid="memo-share-todos">
            {todos.map((t, i) => (
              <li key={i} className="flex items-start gap-3 text-base text-stone-800" data-testid={`memo-share-todo-${i}`}>
                {t.completed
                  ? <Check className="w-4 h-4 mt-1 shrink-0 text-stone-500" aria-hidden="true" />
                  : <Square className="w-4 h-4 mt-1 shrink-0 text-stone-400" aria-hidden="true" />}
                <span className={t.completed ? 'line-through text-stone-500' : ''}>{t.text}</span>
              </li>
            ))}
          </ul>
        )}

        {photos.length > 0 && (
          <div className="mt-8 space-y-4" data-testid="memo-share-photos">
            {photos.map((src, i) => (
              <img key={i} src={src} alt={memo.title || 'Shared photo'} className="w-full rounded-lg bg-stone-200"
                data-testid={`memo-share-photo-${i}`} />
            ))}
          </div>
        )}

        <div className="mt-12 border-t border-stone-200 pt-4 text-xs text-stone-500" data-testid="memo-share-by">
          Shared from KEY-MADE{share?.created_by_name ? ` by ${share.created_by_name}` : ''}
          {share?.created_at ? ` · ${prettyDate(share.created_at)}` : ''}
        </div>
      </div>
    </div>
  );
}
